import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { downloadCsv, normalizeDecimal, DECIMAL_HINT, convertQty, recipeFactor } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { StatusBadge } from "../components/Badges";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { List, Calendar as CalendarIcon, Plus, CheckCircle2, Download, Search, Trash2, ClipboardList, Printer, AlertTriangle, Pencil, CheckSquare, Square, Play, Link2, MoreHorizontal } from "lucide-react";
import FilterPills, { useQueryFilter } from "../components/FilterPills";
import { toast } from "sonner";
import { useConfirm } from "../context/ConfirmContext";
import { openPdf } from "../lib/api";
import LabelPickerButton from "../components/LabelPickerButton";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "../components/ui/dropdown-menu";

export default function Production() {
  const [orders, setOrders] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [restock, setRestock] = useState([]);
  const [form, setForm] = useState({ recipe_id: "", planned_quantity: 1, planned_date: "", notes: "", bundle_code: "" });
  const [completeFor, setCompleteFor] = useState(null);
  const [completeForm, setCompleteForm] = useState({
    actual_quantity: "", output_charge_number: "", output_mhd: "", waste_bags: "",
    packaged_bags: "", packaged_13er: "", packaged_40er: "",
    output_locations: [],
  });

  // Runde 126.5 – Regal-Aufteilung für Fertigware (POPCRN-Standard):
  //   1 Karton = 40 Tüten, 1 Palette = 24 Kartons = 960 Tüten.
  // Der Nutzer kann die vorgeschlagenen Zeilen im Dialog frei
  // anpassen / erweitern.
  const PACKS_PER_KARTON = 40;
  const KARTONS_PER_PALETTE = 24;
  const PALETTE_SIZE = PACKS_PER_KARTON * KARTONS_PER_PALETTE; // 960

  const computeBreakdown = (qty) => {
    const n = Math.max(0, Math.floor(Number(qty) || 0));
    const pallets = Math.floor(n / PALETTE_SIZE);
    let rest = n - pallets * PALETTE_SIZE;
    const kartons = Math.floor(rest / PACKS_PER_KARTON);
    rest = rest - kartons * PACKS_PER_KARTON;
    return { pallets, kartons, loose: rest, total: n };
  };

  const suggestLocations = (qty) => {
    const b = computeBreakdown(qty);
    const rows = [];
    for (let i = 0; i < b.pallets; i++) {
      rows.push({ location: "", quantity: PALETTE_SIZE, kind: "palette" });
    }
    if (b.kartons > 0) rows.push({ location: "", quantity: b.kartons * PACKS_PER_KARTON, kind: `${b.kartons}× Karton` });
    if (b.loose > 0)   rows.push({ location: "", quantity: b.loose, kind: "Einzeltüten" });
    if (rows.length === 0) rows.push({ location: "", quantity: b.total, kind: "" });
    return rows;
  };

  // Sorten-/FEFO-Regalvorschlag für Fertigware (bestätigt: A01 = WG 001 …)
  const [shelfSug, setShelfSug] = useState(null);
  const rackGroupCode = (loc) => {
    const m = String(loc || "").trim().match(/^[A-Za-z]*(\d+)/);
    return m ? String(parseInt(m[1], 10)).padStart(3, "0") : null;
  };
  const loadShelfSuggestion = async (order, planned) => {
    setShelfSug(null);
    if (!order?.output_item_id) return;
    try {
      const { data } = await api.get(`/shelves/suggest-finished`, {
        params: { item_id: order.output_item_id, quantity: planned || 0, mhd: order.output_mhd || "" },
      });
      setShelfSug(data);
      const best = data?.best?.code;
      if (best) {
        setCompleteForm((c) => (c._locations_touched ? c : {
          ...c,
          output_locations: (c.output_locations || []).map((r) => ({ ...r, location: r.location || best })),
        }));
      }
    } catch { /* Vorschlag optional */ }
  };
  const [editOrder, setEditOrder] = useState(null); // production order being edited
  const [editForm, setEditForm] = useState({ planned_quantity: "", planned_date: "", notes: "", bundle_code: "", status: "" });
  const [selected, setSelected] = useState({});

  // Pickliste state
  const [pickOrder, setPickOrder] = useState(null); // {id, number}
  const [pickData, setPickData] = useState(null);
  const [pickLoading, setPickLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [bundles, setBundles] = useState({});
  const [selectedRecipe, setSelectedRecipe] = useState(null);   // full recipe with ingredients+stock
  const [assignFor, setAssignFor] = useState(null);  // production order to assign
  const [assignOrders, setAssignOrders] = useState([]); // available sales orders
  const [assignSelected, setAssignSelected] = useState({});
  const [assignBusy, setAssignBusy] = useState(false);
  const [statusFilter, setStatusFilter] = useQueryFilter("status", "all");
  const [viewMode, setViewMode] = useState("list");   // "list" | "month" | "week"
  const [calMonth, setCalMonth] = useState(() => {
    const d = new Date(); return { year: d.getFullYear(), month: d.getMonth() };
  });
  // Kapazitäts-Setting (persistiert in localStorage) - kg/Tag
  const [dailyCapacity, setDailyCapacity] = useState(() => {
    const v = Number(localStorage.getItem("popcrn.daily_capacity_kg"));
    return v > 0 ? v : 100;
  });
  useEffect(() => { localStorage.setItem("popcrn.daily_capacity_kg", String(dailyCapacity)); }, [dailyCapacity]);
  const [weekStart, setWeekStart] = useState(() => {
    // Montag der aktuellen Woche
    const d = new Date();
    const dow = (d.getDay() + 6) % 7;
    d.setDate(d.getDate() - dow);
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [shortageDlg, setShortageDlg] = useState(null); // {order, shortages}
  const [startBusy, setStartBusy] = useState(false);

  const load = async () => setOrders((await api.get("/production")).data);
  const loadRestock = () => api.get("/production/restock-suggestions").then((r) => setRestock(r.data || [])).catch(() => {});
  const openRestock = (s) => {
    setForm({ recipe_id: s.recipe_id || "", planned_quantity: s.suggested_quantity, planned_date: "", notes: `Nachproduktion ${s.name}`, bundle_code: "" });
    setOpen(true);
  };
  const confirm = useConfirm();

  // Reload full recipe whenever the selection changes
  useEffect(() => {
    if (!form.recipe_id) { setSelectedRecipe(null); return; }
    let cancelled = false;
    api.get(`/recipes/${form.recipe_id}`).then((r) => {
      if (cancelled) return;
      setSelectedRecipe(r.data);
      const mx = Number(r.data?.output_max_stock || 0);
      const st = Number(r.data?.output_item_stock || 0);
      if (mx > 0) {
        setForm((f) => ({ ...f, planned_quantity: Math.max(Math.round(mx - st), 0) }));
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [form.recipe_id]);

  const removeOrder = async (o) => {
    const ok = await confirm({
      title: `Produktionsauftrag ${o.number} löschen?`,
      description: "Nur der Auftragsdatensatz wird entfernt. Chargen und Lagerbestand bleiben unverändert.",
      confirmLabel: "Löschen",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/production/${o.id}`);
      toast.success(`Auftrag ${o.number} gelöscht · Bestand unverändert`);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "object" ? d.message : (d || "Fehler"));
    }
  };

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const toggleSelect = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const toggleAll = () => {
    const filtered = orders.filter((o) => {
      if (!q) return true;
      const s = q.toLowerCase();
      return (o.number || "").toLowerCase().includes(s)
          || (o.recipe_name || "").toLowerCase().includes(s)
          || (o.output_item_name || "").toLowerCase().includes(s)
          || (o.status || "").toLowerCase().includes(s);
    });
    const allOn = filtered.length > 0 && filtered.every((r) => selected[r.id]);
    const next = { ...selected };
    filtered.forEach((r) => { next[r.id] = !allOn; });
    setSelected(next);
  };

  const bulkDelete = async () => {
    if (!selectedIds.length) return;
    const ok = await confirm({
      title: `${selectedIds.length} Produktionsauftrag/-aufträge löschen?`,
      description: "Nur die Auftragsdatensätze werden entfernt. Chargen und Lagerbestand bleiben unverändert – auch bei abgeschlossenen Aufträgen.",
      confirmLabel: `${selectedIds.length}× löschen`,
      variant: "danger",
    });
    if (!ok) return;
    try {
      const { data } = await api.post("/production/bulk-delete", { ids: selectedIds });
      toast.success(`${data.deleted}/${data.total} gelöscht · Bestand unverändert`);
      setSelected({});
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Bulk-Löschen");
    }
  };

  const openEdit = (o) => {
    setEditOrder(o);
    setEditForm({
      planned_quantity: o.planned_quantity ?? "",
      planned_date: o.planned_date || "",
      notes: o.notes || "",
      bundle_code: o.bundle_code || "",
      status: o.status || "planned",
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editOrder) return;
    try {
      const body = {
        planned_quantity: editForm.planned_quantity !== "" ? Number(editForm.planned_quantity) : null,
        planned_date: editForm.planned_date || null,
        notes: editForm.notes || null,
        bundle_code: editForm.bundle_code || null,
        status: editForm.status || null,
      };
      await api.patch(`/production/${editOrder.id}`, body);
      toast.success(`Auftrag ${editOrder.number} aktualisiert · Bestand unverändert`);
      setEditOrder(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Speichern");
    }
  };
  useEffect(() => { load(); loadRestock(); api.get("/recipes").then((r) => setRecipes(r.data)); api.get("/settings/bundles").then((r) => setBundles(r.data)).catch(() => {}); }, []);

  // Vorbefüllten Nachproduktions-Dialog öffnen, wenn per ?restock=<item_id> verlinkt (z.B. vom Dashboard).
  const location = useLocation();
  const navigate = useNavigate();
  const restockHandled = useRef(false);
  useEffect(() => {
    const id = new URLSearchParams(location.search).get("restock");
    if (!id || restockHandled.current || restock.length === 0) return;
    const s = restock.find((r) => r.item_id === id);
    if (s) {
      restockHandled.current = true;
      openRestock(s);
      navigate("/produktion", { replace: true });
    }
  }, [location.search, restock]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/production", { ...form, planned_quantity: Number(form.planned_quantity), bundle_code: form.bundle_code || null });
      toast.success("Produktionsauftrag angelegt");
      setOpen(false); setForm({ recipe_id: "", planned_quantity: 1, planned_date: "", notes: "", bundle_code: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const [boxShortageDlg, setBoxShortageDlg] = useState(null); // Runde 126.8

  const complete = async (e, force = false) => {
    e?.preventDefault?.();
    const actual = Number(completeForm.actual_quantity) || 0;
    const waste = Number(completeForm.waste_bags) || 0;
    // Runde 126.5 – Sanity-Check: Summe der Regal-Zeilen muss die
    // Ist-Menge treffen (Warnung, kein harter Block).
    const locs = (completeForm.output_locations || []).filter((r) => (r.location || "").trim() && Number(r.quantity) > 0);
    const locSum = locs.reduce((s, r) => s + Number(r.quantity), 0);
    if (actual > 0 && locs.length > 0 && Math.abs(locSum - actual) > 0.0001 && !completeForm._confirmed) {
      setCompleteForm({ ...completeForm, _confirmed: true });
      toast.warning(`Summe Regalzeilen (${locSum}) ≠ Ist-Menge (${actual}). Nochmal auf „Abschließen" klicken zum Bestätigen.`);
      return;
    }
    try {
      await api.post(`/production/${completeFor}/complete`, {
        actual_quantity: completeForm.actual_quantity ? Number(completeForm.actual_quantity) : null,
        output_charge_number: completeForm.output_charge_number || null,
        output_mhd: completeForm.output_mhd || null,
        waste_bags: waste || null,
        packaged_bags: completeForm.packaged_bags !== "" ? Number(completeForm.packaged_bags) : null,
        packaged_13er: completeForm.packaged_13er !== "" ? Number(completeForm.packaged_13er) : null,
        packaged_40er: completeForm.packaged_40er !== "" ? Number(completeForm.packaged_40er) : null,
        output_locations: locs.length ? locs.map((r) => ({ location: r.location.trim(), quantity: Number(r.quantity), kind: r.kind || null })) : null,
        output_location: locs.length ? locs[0].location.trim() : null,
        force: force || null,
      });
      toast.success(`Produktion abgeschlossen – Charge erstellt${waste > 0 ? ` (Ausschuss: ${waste} Tüten)` : ""}`);
      // Runde 126.7 – Abschluss-PDF direkt zum Drucken öffnen.
      openPdf(`/production/${completeFor}/completion.pdf`);
      const closed = completeFor;
      setCompleteFor(null);
      setCompleteForm({
        actual_quantity: "", output_charge_number: "", output_mhd: "", waste_bags: "",
        packaged_bags: "", packaged_13er: "", packaged_40er: "",
        output_locations: [],
      });
      void closed;
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.code === "box_shortage") {
        setBoxShortageDlg({ shortages: detail.shortages || [] });
        return;
      }
      toast.error(detail?.message || detail || "Fehler");
    }
  };

  const forceComplete = async () => {
    setBoxShortageDlg(null);
    await complete(null, true);
  };

  const startProduction = async (o, force = false) => {
    setStartBusy(true);
    try {
      await api.post(`/production/${o.id}/start`, { force });
      toast.success(`Auftrag ${o.number} gestartet – Status: In Produktion${force ? " (trotz Fehlmenge)" : ""}`);
      setShortageDlg(null);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "shortage") {
        setShortageDlg({ order: o, shortages: d.shortages || [] });
      } else {
        toast.error(typeof d === "object" ? d.message : (d || "Start fehlgeschlagen"));
      }
    } finally { setStartBusy(false); }
  };

  const openAssignDialog = async (o) => {
    setAssignFor(o);
    setAssignSelected({});
    try {
      const { data } = await api.get("/orders");
      const list = Array.isArray(data) ? data : (data.data || data.orders || []);
      setAssignOrders(list.filter((so) =>
        so.status !== "shipped" && so.status !== "cancelled"
      ));
    } catch {
      setAssignOrders([]);
    }
  };

  const submitAssign = async () => {
    if (!assignFor) return;
    const ids = Object.keys(assignSelected).filter((k) => assignSelected[k]);
    if (!ids.length) { toast.error("Mindestens eine Bestellung auswählen"); return; }
    setAssignBusy(true);
    try {
      const { data } = await api.post(`/production/${assignFor.id}/assign-order`,
                                       { order_ids: ids });
      toast.success(`Charge ${data.batch?.charge_number} an ${data.assigned.length} Bestellung(en) zugewiesen`);
      setAssignFor(null); setAssignSelected({});
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Zuweisung fehlgeschlagen");
    } finally { setAssignBusy(false); }
  };

  const openPicklist = async (o) => {
    setPickOrder(o);
    setPickData(null);
    setPickLoading(true);
    try {
      const { data } = await api.get(`/production/${o.id}/picklist`);
      setPickData(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Laden der Pickliste");
      setPickOrder(null);
    } finally {
      setPickLoading(false);
    }
  };

  const printPicklist = async (o) => {
    try {
      await openPdf(`/production/${o.id}/picklist.pdf`);
    } catch (err) { toast.error("PDF konnte nicht geladen werden"); }
  };

  const confirmPick = async () => {
    if (!pickOrder) return;
    const ok = await confirm({
      title: `Pickliste ${pickOrder.number} bestätigen?`,
      description: pickData?.any_shortage
        ? "Der Bestand einiger Zutaten reicht NICHT aus. Bestätigen bucht trotzdem – Fehlmengen werden separat protokolliert."
        : "Der Lagerbestand wird jetzt abgezogen (FEFO). Dieser Schritt kann nicht rückgängig gemacht werden.",
      confirmLabel: "Bestand jetzt abziehen",
      variant: pickData?.any_shortage ? "danger" : "default",
    });
    if (!ok) return;
    setConfirming(true);
    try {
      const { data } = await api.post(`/production/${pickOrder.id}/confirm-pick`);
      toast.success(`Pickliste bestätigt · ${data.lines.length} Zutaten gebucht`
        + (data.total_shortage > 0 ? ` · Fehlmenge ${data.total_shortage}` : ""));
      setPickOrder(null);
      setPickData(null);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "object" ? d.message : (d || "Fehler"));
    } finally {
      setConfirming(false);
    }
  };

  const previewBlock = (() => {
    const rec = selectedRecipe && selectedRecipe.id === form.recipe_id
      ? selectedRecipe
      : recipes.find((r) => r.id === form.recipe_id);
    const qty = Number(form.planned_quantity);
    if (!rec || !(qty > 0)) return null;
    const outQty = Number(rec.output_quantity || 1) || 1;
    const outUnit = rec.output_unit || "";
    const outName = rec.output_item_name || "Fertigware";
    const weightG = Number(selectedRecipe?.output_item_weight_g || 0);
    // Geplante Menge = Einheiten der Fertigware, gleiche Regel wie das Backend.
    const ingredientFactor = recipeFactor(qty, outQty, outUnit, weightG);
    const perUnitLabel = weightG > 0 && ["g", "ml", "kg", "l"].includes(outUnit.trim().toLowerCase())
      ? `${weightG} g` : `${outQty}${outUnit}`;
    const totalMass = ingredientFactor * outQty;
    let totalStr = `${totalMass.toLocaleString("de-DE")} ${outUnit}`.trim();
    if (outUnit === "g" && totalMass >= 1000) totalStr = `${(totalMass / 1000).toLocaleString("de-DE", { maximumFractionDigits: 2 })} kg`;
    if (outUnit === "ml" && totalMass >= 1000) totalStr = `${(totalMass / 1000).toLocaleString("de-DE", { maximumFractionDigits: 2 })} l`;
    const bundleCfg = form.bundle_code && bundles[form.bundle_code];
    const bundleUnits = bundleCfg?.units || 0;
    const bundleLabel = bundleCfg?.label || "";
    const numBundles = bundleUnits > 0 ? qty / bundleUnits : 0;
    const fullBundles = Math.floor(numBundles);
    const remainder = bundleUnits > 0 ? qty - fullBundles * bundleUnits : 0;
    const unitWord = outUnit === "stk" ? "Stück" : outUnit === "ml" ? "Flasche(n)" : "Packung(en)";
    // Bundles bündeln nur Fertigware, Tüte = 1 je Einheit, Bestand in Rezept-Einheit umrechnen.
    const bagItemId = selectedRecipe?.bag_item_id;
    const ingredients = (selectedRecipe?.ingredients || []).map((ing) => {
      const isBag = bagItemId && ing.item_id === bagItemId;
      const unit = isBag ? "stk" : ing.unit;
      const needed = isBag ? qty : Number(ing.quantity || 0) * ingredientFactor;
      const stock = convertQty(Number(ing.stock ?? 0), ing.item_unit || unit, unit);
      const short = Math.max(needed - stock, 0);
      return { ...ing, unit, needed, stock, short };
    });
    const anyShort = ingredients.some((i) => i.short > 0);
    return (
      <div data-testid="production-preview"
           className="rounded-xl border-2 border-[var(--pop-yellow)] bg-[var(--pop-yellow)]/10 p-3 space-y-2 text-sm">
        <div className="overline text-xs">Vorschau</div>
        <div className="font-semibold">
          <span className="font-mono" data-testid="preview-units">{qty.toLocaleString("de-DE")}</span> × {perUnitLabel} = <span className="font-mono text-[var(--pop-pink)]" data-testid="preview-total-mass">{totalStr}</span> {outName}
        </div>
        <div className="text-xs text-[var(--text-muted)]">
          Ergibt {qty.toLocaleString("de-DE")} {unitWord} à {perUnitLabel}
        </div>
        {bundleUnits > 0 && (
          <div className="text-xs pt-1 border-t border-[var(--pop-yellow)]/40" data-testid="preview-bundles">
            Bundle {bundleLabel}: {fullBundles.toLocaleString("de-DE")} volle {bundleLabel}(s)
            {remainder > 0 && ` + ${remainder} einzeln`}
            {" "}({numBundles.toLocaleString("de-DE", { maximumFractionDigits: 2 })}× {bundleUnits}er)
          </div>
        )}
        {ingredients.length > 0 && (
          <div className="pt-2 border-t border-[var(--pop-yellow)]/40" data-testid="preview-ingredients">
            <div className="overline text-xs mb-1 flex items-center gap-2">
              Zutaten-Bedarf
              {anyShort && (
                <span className="pop-pill bg-red-100 text-red-700 text-[10px] font-bold inline-flex items-center gap-1"
                      data-testid="preview-shortage-warning">
                  <AlertTriangle size={10} /> Fehlmenge
                </span>
              )}
            </div>
            <div className="space-y-1">
              {ingredients.map((ing, i) => {
                const isShort = ing.short > 0;
                return (
                  <div key={i} className="grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center text-xs"
                       data-testid={`preview-ing-${i}`}>
                    <span className="truncate">{ing.item_name || ing.item_sku || "?"}</span>
                    <span className="font-mono" data-testid={`preview-ing-needed-${i}`}>
                      {ing.needed.toLocaleString("de-DE", { maximumFractionDigits: 3 })} {ing.unit}
                    </span>
                    <span className={`font-mono text-[var(--text-muted)]`}>
                      / {ing.stock.toLocaleString("de-DE", { maximumFractionDigits: 3 })} {ing.unit}
                    </span>
                    {isShort ? (
                      <span className="pop-pill bg-red-100 text-red-700 text-[10px] font-bold whitespace-nowrap"
                            data-testid={`preview-ing-short-${i}`}>
                        −{ing.short.toLocaleString("de-DE", { maximumFractionDigits: 3 })} {ing.unit}
                      </span>
                    ) : (
                      <span className="pop-pill bg-green-100 text-green-700 text-[10px] font-bold">OK</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  })();

  return (
    <div data-testid="page-production">
      <PageHeader
        eyebrow="MRP"
        title="Produktionsaufträge"
        description="Plane Chargen, buche Materialverbrauch automatisch, erzeuge fertige Ware inkl. neuer Charge & MHD."
        actions={
          <div className="flex items-center gap-2">
            <Button data-testid="btn-export-production" variant="outline" className="rounded-full"
                    onClick={() => downloadCsv("/exports/production.csv", "produktion.csv")}>
              <Download size={16} className="mr-1" /> CSV
            </Button>
            {selectedIds.length > 0 && (
              <Button data-testid="btn-bulk-delete-production" variant="outline"
                      onClick={bulkDelete}
                      className="rounded-full border-red-300 text-red-700 hover:bg-red-50">
                <Trash2 size={14} className="mr-1" /> {selectedIds.length}× löschen
              </Button>
            )}
            <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="btn-new-production" className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
                <Plus size={16} className="mr-1" /> Neuer Auftrag
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display text-2xl">Produktionsauftrag</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-4">
                <div>
                  <Label>Rezeptur *</Label>
                  <Select value={form.recipe_id} onValueChange={(v) => setForm({ ...form, recipe_id: v })} required>
                    <SelectTrigger data-testid="select-production-recipe"><SelectValue placeholder="Rezeptur…" /></SelectTrigger>
                    <SelectContent>{recipes.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Geplante Menge *</Label><Input required type="number" step="any" placeholder={DECIMAL_HINT} data-testid="input-production-qty" value={form.planned_quantity} onChange={(e) => setForm({ ...form, planned_quantity: normalizeDecimal(e.target.value) })} />
                    {selectedRecipe?.output_max_stock > 0 && (
                      <div className="text-[11px] mt-1" data-testid="stock-plan-hint">
                        Bestand {Number(selectedRecipe.output_item_stock || 0).toLocaleString("de-DE")} · Min {Number(selectedRecipe.output_min_stock || 0).toLocaleString("de-DE")} · Max {Number(selectedRecipe.output_max_stock || 0).toLocaleString("de-DE")}
                        {Number(selectedRecipe.output_item_stock || 0) < Number(selectedRecipe.output_min_stock || 0)
                          ? <span className="text-red-600 font-bold"> · unter Mindestbestand! Vorschlag bis Maximum</span>
                          : <span className="text-[var(--text-muted)]"> · Vorschlag = Maximum − Bestand</span>}
                      </div>
                    )}
                  </div>
                  <div><Label>Plandatum</Label><Input type="date" value={form.planned_date} onChange={(e) => setForm({ ...form, planned_date: e.target.value })} /></div>
                </div>
                {previewBlock}
                <div>
                  <Label>Bundle-Größe (optional)</Label>
                  <Select value={form.bundle_code || "none"} onValueChange={(v) => setForm({ ...form, bundle_code: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="select-production-bundle"><SelectValue placeholder="Kein Bundle (Einzelprodukt)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Einzelprodukt (kein Bundle)</SelectItem>
                      {Object.entries(bundles).map(([code, cfg]) => (
                        <SelectItem key={code} value={code}>{cfg.label} · {cfg.units} Einheiten</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="text-xs text-[var(--text-muted)] mt-1">Bundle multipliziert die Rezeptur automatisch. Verpackungsmaterial wird vom Lager abgezogen.</div>
                </div>
                <div><Label>Notizen</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                <DialogFooter>
                  <Button type="submit" data-testid="btn-save-production" className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">Speichern</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        }
      />

      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <Input placeholder="Suche Nr., Rezeptur, Fertigware…" value={q} onChange={(e) => setQ(e.target.value)}
                 className="pl-8 rounded-xl h-9" data-testid="input-production-search" />
        </div>
        <div className="flex items-center gap-1 text-xs">
        <FilterPills
          options={[
            ["all", "Alle", "#e5e5e5"],
            ["planned", "Geplant", "#e5e5e5"],
            ["in_progress", "In Produktion", "#fef3c7"],
            ["completed", "Abgeschlossen", "#d1fae5"],
          ]}
          value={statusFilter}
          onChange={setStatusFilter}
        />
        </div>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <button data-testid="btn-goto-board" onClick={() => navigate("/produktion/board")}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[var(--pop-pink)] text-white px-4 h-9 text-xs font-bold hover:opacity-90 transition-opacity">
            Aufträge &amp; Wochenplan
          </button>
          <label className="inline-flex items-center gap-1 text-xs text-[var(--text-muted)]" title="Tageskapazität in kg für Auslastungs-Anzeige">
            Kapazität:
            <input type="number" min="1" data-testid="input-daily-capacity"
                   value={dailyCapacity}
                   onChange={(e) => setDailyCapacity(Math.max(1, Number(e.target.value) || 1))}
                   className="w-16 h-7 rounded border border-[var(--border)] px-1.5 text-xs font-mono text-right" />
            <span>kg/Tag</span>
          </label>
          <div className="flex items-center rounded-full bg-[var(--pop-cream)]/40 p-1 gap-0.5">
            <button data-testid="view-list" onClick={() => setViewMode("list")}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1 ${viewMode === "list" ? "bg-white shadow" : "text-[var(--text-muted)]"}`}>
              <List size={13} /> Liste
            </button>
            <button data-testid="view-week" onClick={() => setViewMode("week")}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1 ${viewMode === "week" ? "bg-white shadow" : "text-[var(--text-muted)]"}`}>
              <CalendarIcon size={13} /> Woche
            </button>
            <button data-testid="view-calendar" onClick={() => setViewMode("month")}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1 ${viewMode === "month" ? "bg-white shadow" : "text-[var(--text-muted)]"}`}>
              <CalendarIcon size={13} /> Monat
            </button>
          </div>
        </div>
      </div>

      {viewMode === "month" && (() => {
        const filteredForCal = orders.filter((o) => {
          if (statusFilter !== "all" && o.status !== statusFilter) return false;
          return true;
        });
        // Kapazitäts-Hilfsfunktion: kg-Menge pro Auftrag
        const orderKg = (o) => {
          const q = Number(o.actual_quantity ?? o.planned_quantity ?? 0);
          const outQty = Number(o.recipe_output_quantity ?? 0.1); // fallback 100g
          // ohne recipe-output_quantity nutzen wir planned_quantity direkt als kg
          const raw = q * outQty;
          return raw > 0 ? raw / 1000 : q * 0.1;
        };
        const { year, month } = calMonth;
        const first = new Date(year, month, 1);
        const last = new Date(year, month + 1, 0);
        const daysInMonth = last.getDate();
        const weekdayOfFirst = (first.getDay() + 6) % 7;
        const cells = [];
        for (let i = 0; i < weekdayOfFirst; i++) cells.push(null);
        for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
        while (cells.length % 7 !== 0) cells.push(null);
        const byDay = {};
        for (const o of filteredForCal) {
          const key = (o.planned_date || "").slice(0, 10);
          if (!key) continue;
          (byDay[key] = byDay[key] || []).push(o);
        }
        const monthName = new Date(year, month, 1).toLocaleDateString("de-DE",
          { month: "long", year: "numeric" });
        const bg = (s) => s === "in_progress" ? "#fef3c7"
                      : s === "completed" ? "#d1fae5"
                      : s === "cancelled" ? "#fee2e2" : "#e5e5e5";
        const fg = (s) => s === "in_progress" ? "#854d0e"
                      : s === "completed" ? "#065f46"
                      : s === "cancelled" ? "#991b1b" : "#171717";
        const today = new Date().toISOString().slice(0, 10);
        return (
          <div className="bg-white border border-[var(--border)] rounded-2xl p-3" data-testid="calendar-view">
            <div className="flex items-center justify-between mb-3">
              <button data-testid="cal-prev"
                      onClick={() => setCalMonth((c) => c.month === 0 ? { year: c.year - 1, month: 11 } : { year: c.year, month: c.month - 1 })}
                      className="rounded-full px-3 py-1 hover:bg-[var(--pop-cream)]/40">←</button>
              <div className="font-display text-lg capitalize">{monthName}</div>
              <button data-testid="cal-next"
                      onClick={() => setCalMonth((c) => c.month === 11 ? { year: c.year + 1, month: 0 } : { year: c.year, month: c.month + 1 })}
                      className="rounded-full px-3 py-1 hover:bg-[var(--pop-cream)]/40">→</button>
            </div>
            <div className="grid grid-cols-7 gap-1 text-[10px] text-[var(--text-muted)] overline mb-1">
              {["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"].map((d) => <div key={d} className="text-center">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {cells.map((cell, i) => {
                if (!cell) return <div key={i} className="min-h-[110px] rounded-lg" />;
                const iso = cell.toISOString().slice(0, 10);
                const items = byDay[iso] || [];
                const totalKg = items.reduce((s, o) => s + orderKg(o), 0);
                const pct = Math.min(100, Math.round((totalKg / dailyCapacity) * 100));
                const barColor = pct >= 100 ? "#dc2626" : pct >= 80 ? "#f59e0b" : "#22c55e";
                const isToday = iso === today;
                return (
                  <div key={i} data-testid={`cal-day-${iso}`}
                       className={`min-h-[110px] rounded-lg border p-1.5 text-xs flex flex-col ${isToday ? "border-[var(--pop-pink)] ring-1 ring-[var(--pop-pink)]/30" : "border-[var(--border)]"}`}>
                    <div className="font-mono font-bold text-[11px] mb-1 flex items-center justify-between">
                      <span>{cell.getDate()}</span>
                      {items.length > 0 && (
                        <span className={`text-[9px] font-bold`} style={{ color: barColor }} data-testid={`cal-load-${iso}`}>
                          {totalKg.toFixed(1)}kg
                        </span>
                      )}
                    </div>
                    <div className="space-y-0.5 flex-1">
                      {items.slice(0, 3).map((o) => (
                        <div key={o.id} data-testid={`cal-order-${o.number}`}
                             title={`${o.number} · ${o.output_item_name} · ${o.planned_quantity}`}
                             style={{ background: bg(o.status), color: fg(o.status) }}
                             className="rounded px-1 py-0.5 text-[10px] font-semibold truncate cursor-pointer hover:opacity-80"
                             onClick={() => openEdit(o)}>
                          {o.number}: {o.output_item_name}
                        </div>
                      ))}
                      {items.length > 3 && (
                        <div className="text-[9px] text-[var(--text-muted)]">+{items.length - 3} mehr</div>
                      )}
                    </div>
                    {items.length > 0 && (
                      <div className="mt-1" title={`Auslastung: ${pct}% von ${dailyCapacity}kg`}>
                        <div className="h-1 rounded-full bg-[var(--border)]/50 overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${Math.min(100, pct)}%`, background: barColor }} />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mt-3 flex items-center gap-4 text-[10px] text-[var(--text-muted)] flex-wrap">
              <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm" style={{ background: "#e5e5e5" }} /> Geplant</span>
              <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm" style={{ background: "#fef3c7" }} /> In Produktion</span>
              <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm" style={{ background: "#d1fae5" }} /> Abgeschlossen</span>
              <span className="mx-2">·</span>
              <span className="inline-flex items-center gap-1"><span className="w-3 h-1 rounded-sm" style={{ background: "#22c55e" }} /> &lt;80% Auslastung</span>
              <span className="inline-flex items-center gap-1"><span className="w-3 h-1 rounded-sm" style={{ background: "#f59e0b" }} /> 80–99%</span>
              <span className="inline-flex items-center gap-1"><span className="w-3 h-1 rounded-sm" style={{ background: "#dc2626" }} /> ≥100% Überlast</span>
            </div>
          </div>
        );
      })()}

      {viewMode === "week" && (() => {
        const filteredForCal = orders.filter((o) => {
          if (statusFilter !== "all" && o.status !== statusFilter) return false;
          return true;
        });
        const orderKg = (o) => {
          const q = Number(o.actual_quantity ?? o.planned_quantity ?? 0);
          return q * 0.1; // fallback 100g/Einheit → kg
        };
        const days = [];
        for (let i = 0; i < 7; i++) {
          const d = new Date(weekStart); d.setDate(d.getDate() + i);
          days.push(d);
        }
        const byDay = {};
        for (const o of filteredForCal) {
          const key = (o.planned_date || "").slice(0, 10);
          if (!key) continue;
          (byDay[key] = byDay[key] || []).push(o);
        }
        const bg = (s) => s === "in_progress" ? "#fef3c7"
                      : s === "completed" ? "#d1fae5"
                      : s === "cancelled" ? "#fee2e2" : "#e5e5e5";
        const fg = (s) => s === "in_progress" ? "#854d0e"
                      : s === "completed" ? "#065f46"
                      : s === "cancelled" ? "#991b1b" : "#171717";
        const today = new Date().toISOString().slice(0, 10);
        // Zeit-Slots 06:00 - 18:00 (13 Stunden)
        const slots = Array.from({ length: 13 }, (_, i) => 6 + i);
        const weekLabel = `${days[0].toLocaleDateString("de-DE", { day: "2-digit", month: "short" })} – ${days[6].toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" })}`;
        return (
          <div className="bg-white border border-[var(--border)] rounded-2xl p-3" data-testid="week-view">
            <div className="flex items-center justify-between mb-3">
              <button data-testid="week-prev"
                      onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d); }}
                      className="rounded-full px-3 py-1 hover:bg-[var(--pop-cream)]/40">← Woche</button>
              <div className="font-display text-lg">{weekLabel}</div>
              <button data-testid="week-next"
                      onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d); }}
                      className="rounded-full px-3 py-1 hover:bg-[var(--pop-cream)]/40">Woche →</button>
            </div>
            <div className="grid gap-1" style={{ gridTemplateColumns: "48px repeat(7, 1fr)" }}>
              {/* Header */}
              <div />
              {days.map((d, i) => {
                const iso = d.toISOString().slice(0, 10);
                const items = byDay[iso] || [];
                const totalKg = items.reduce((s, o) => s + orderKg(o), 0);
                const pct = Math.min(100, Math.round((totalKg / dailyCapacity) * 100));
                const barColor = pct >= 100 ? "#dc2626" : pct >= 80 ? "#f59e0b" : "#22c55e";
                const isToday = iso === today;
                return (
                  <div key={i} className={`text-center pb-1 ${isToday ? "text-[var(--pop-pink)] font-bold" : ""}`}>
                    <div className="overline text-[10px]">{d.toLocaleDateString("de-DE", { weekday: "short" })}</div>
                    <div className="font-mono text-sm">{d.getDate()}.</div>
                    {items.length > 0 && (
                      <div className="mt-0.5 px-2" title={`${totalKg.toFixed(1)}kg / ${dailyCapacity}kg`}>
                        <div className="h-1 rounded-full bg-[var(--border)]/50 overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${Math.min(100, pct)}%`, background: barColor }} />
                        </div>
                        <div className="text-[9px] font-mono font-bold mt-0.5" style={{ color: barColor }} data-testid={`week-load-${iso}`}>
                          {totalKg.toFixed(1)}/{dailyCapacity}kg
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {/* Time slots grid */}
              {slots.map((h) => (
                <>
                  <div key={`t-${h}`} className="text-[10px] text-[var(--text-muted)] font-mono pr-1 text-right pt-1">
                    {String(h).padStart(2, "0")}:00
                  </div>
                  {days.map((d, i) => {
                    const iso = d.toISOString().slice(0, 10);
                    const items = h === 6 ? (byDay[iso] || []) : []; // Aufträge auf 06:00-Slot stapeln
                    const isToday = iso === today;
                    return (
                      <div key={`c-${h}-${i}`}
                           className={`min-h-[28px] border-t border-[var(--border)]/40 ${isToday ? "bg-[var(--pop-pink)]/5" : ""} ${h === 12 ? "bg-[var(--pop-cream)]/20" : ""}`}>
                        {items.map((o) => (
                          <div key={o.id} data-testid={`week-order-${o.number}`}
                               onClick={() => openEdit(o)}
                               style={{ background: bg(o.status), color: fg(o.status) }}
                               className="rounded px-1 py-0.5 text-[10px] font-semibold truncate cursor-pointer hover:opacity-80 m-0.5"
                               title={`${o.number} · ${o.output_item_name} · ${o.planned_quantity}`}>
                            {o.number}: {o.output_item_name}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </>
              ))}
            </div>
          </div>
        );
      })()}

      {restock.length > 0 && (
        <div data-testid="restock-overview" className="mb-4 rounded-2xl border border-amber-300 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={18} className="text-amber-600 dark:text-amber-400" />
            <h3 className="font-display font-bold">Nachproduktion nötig ({restock.length})</h3>
            <span className="text-xs text-[var(--text-muted)]">Fertigware unter Mindestbestand · Vorschlag bis Maximum</span>
          </div>
          <div className="space-y-2">
            {restock.map((s) => (
              <div key={s.item_id} data-testid={`restock-${s.item_id}`} className="flex items-center gap-3 rounded-xl bg-[var(--surface)] border border-[var(--border)] p-3">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm truncate">{s.name}</div>
                  <div className="text-[11px] text-[var(--text-muted)] font-mono">Bestand {s.stock} · Min {s.min_stock}{s.max_stock ? ` · Max ${s.max_stock}` : ""} {s.unit}</div>
                  {Number(s.planned) > 0 && (
                    <div className={`text-[11px] font-mono mt-0.5 ${s.covered ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"}`} data-testid={`restock-planned-${s.item_id}`}>
                      bereits {Number(s.planned).toLocaleString("de-DE")} {s.unit} in Produktion geplant{s.covered ? " · Bedarf gedeckt" : ""}
                    </div>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] text-[var(--text-muted)]">Vorschlag</div>
                  <div className="font-mono font-bold text-sm">{Number(s.suggested_quantity).toLocaleString("de-DE")} {s.unit}</div>
                </div>
                <button data-testid={`restock-order-${s.item_id}`} disabled={!s.recipe_id} onClick={() => openRestock(s)}
                        title={s.recipe_id ? "Auftrag anlegen (bis Maximum)" : "Keine Rezeptur hinterlegt"}
                        className="shrink-0 inline-flex items-center gap-1 rounded-full bg-[var(--pop-caramel)] text-white px-4 h-9 text-xs font-bold hover:opacity-90 disabled:opacity-40">
                  <Plus size={13} /> Auftrag
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {viewMode === "list" && (() => {
        const filtered = orders.filter((o) => {
          if (statusFilter !== "all" && o.status !== statusFilter) return false;
          if (!q) return true;
          const s = q.toLowerCase();
          return (o.number || "").toLowerCase().includes(s)
              || (o.recipe_name || "").toLowerCase().includes(s)
              || (o.output_item_name || "").toLowerCase().includes(s)
              || (o.status || "").toLowerCase().includes(s);
        });
        const stripe = (s) => s === "in_progress" ? "var(--pop-caramel)"
                      : s === "completed" ? "var(--success)"
                      : s === "cancelled" ? "#dc2626" : "var(--text-muted)";
        return (
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl overflow-hidden">
          <div className="p-3 border-b border-[var(--border)] flex items-center gap-2">
            <button onClick={toggleAll} data-testid="production-select-all" className="shrink-0 hover:opacity-70" title="Alle auswählen">
              {orders.length > 0 && orders.every((r) => selected[r.id])
                ? <CheckSquare size={18} className="text-[var(--pop-pink)]" />
                : <Square size={18} className="text-[var(--text-muted)]" />}
            </button>
            <span className="text-xs text-[var(--text-muted)] ml-auto">{filtered.length} / {orders.length}</span>
          </div>
          <div className="p-3 space-y-2" data-testid="production-list">
            {filtered.map((o) => (
              <div key={o.id} data-testid={`production-card-${o.number}`}
                   style={{ borderLeftColor: stripe(o.status) }}
                   className={`rounded-xl border border-l-4 p-4 transition-colors ${selected[o.id] ? "border-[var(--pop-pink)] bg-[var(--surface-alt)]" : "border-[var(--border)] bg-[var(--surface)]"}`}>
                <div className="flex items-start gap-3">
                  <div className="pt-0.5">
                    <button onClick={() => toggleSelect(o.id)} data-testid={`select-prod-${o.number}`} className="hover:opacity-70">
                      {selected[o.id] ? <CheckSquare size={18} className="text-[var(--pop-pink)]" /> : <Square size={18} className="text-[var(--text-muted)]" />}
                    </button>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-display font-bold text-base truncate min-w-0 flex items-center gap-2">
                        <span className="truncate">{o.output_item_name || o.recipe_name || "—"}</span>
                        {o.bundle_code && <span className="pop-pill bg-pink-100 text-pink-800 text-[10px] font-bold shrink-0" data-testid={`bundle-badge-${o.number}`}>{o.bundle_code}</span>}
                      </div>
                      <StatusBadge status={o.status} />
                    </div>
                    <div className="flex items-center gap-2 flex-wrap mt-0.5 text-xs text-[var(--text-muted)] font-mono">
                      <span>{o.number}</span>
                      <span>· {o.recipe_name}</span>
                      <span>· {o.actual_quantity ?? o.planned_quantity} Stk</span>
                    </div>
                    {o.assigned_orders_details && o.assigned_orders_details.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1" data-testid={`assigned-to-${o.number}`}>
                        {o.assigned_orders_details.map((so) => (
                          <a key={so.id} href="/bestellungen" title={`Bestellung ${so.number} · ${so.customer_name || ""}`}
                             className="pop-pill bg-pink-100 text-pink-800 text-[9px] font-bold hover:bg-pink-200 cursor-pointer">→ {so.number}</a>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-3 flex-wrap">
                      {o.status === "planned" && (
                        <button onClick={() => startProduction(o)} data-testid={`btn-start-${o.number}`}
                                className="inline-flex items-center gap-1 rounded-full bg-[var(--pop-caramel)] text-white px-4 h-8 text-xs font-bold hover:opacity-90">
                          <Play size={13} /> Start
                        </button>
                      )}
                      {o.status !== "completed" && (
                        <button
                          onClick={() => {
                            const planned = Number(o.planned_quantity) || 0;
                            setCompleteFor(o.id);
                            setCompleteForm({
                              actual_quantity: o.planned_quantity,
                              output_charge_number: o.output_charge_number || `P-${o.number}`,
                              output_mhd: o.output_mhd || "",
                              waste_bags: "",
                              output_location: o.default_location || "",
                              output_locations: suggestLocations(planned),
                            });
                            loadShelfSuggestion(o, planned);
                          }}
                          data-testid={`btn-complete-${o.number}`}
                          className="inline-flex items-center gap-1 rounded-full bg-[var(--success)] text-white px-4 h-8 text-xs font-bold hover:opacity-90">
                          <CheckCircle2 size={13} /> Abschließen
                        </button>
                      )}
                      {o.status === "completed" && o.output_batch_id && (
                        <button onClick={() => openAssignDialog(o)} data-testid={`btn-assign-${o.number}`}
                                className="inline-flex items-center gap-1 rounded-full bg-[var(--pop-pink)] text-white px-4 h-8 text-xs font-bold hover:opacity-90">
                          <Link2 size={13} /> Zuweisen
                        </button>
                      )}
                      {o.status === "completed" && (
                        <LabelPickerButton
                          testid={`label-picker-${o.number}`}
                          label="Etikett"
                          category="produktion"
                          data={{
                            sku: o.output_item_sku || o.output_item_name,
                            item_name: o.output_item_name,
                            charge_number: o.output_charge_number,
                            mhd: o.output_mhd,
                            quantity: `${o.actual_quantity} ${o.output_item_unit || "stk"}`,
                            barcode: o.output_charge_number,
                          }}
                        />
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button data-testid={`prod-menu-${o.number}`} title="Weitere Aktionen"
                                  className="ml-auto h-8 w-8 inline-flex items-center justify-center rounded-full border border-[var(--border)] text-[var(--text-muted)] hover:text-[var(--text)] hover:border-[var(--pop-pink)]">
                            <MoreHorizontal size={16} />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52">
                          <DropdownMenuItem data-testid={`btn-picklist-${o.number}`} onClick={() => openPicklist(o)}>
                            <ClipboardList size={14} className="mr-2" /> Pickliste{o.picked_at ? " ✓" : ""}
                          </DropdownMenuItem>
                          {o.bundle_code && (
                            <DropdownMenuItem data-testid={`btn-bundle-label-${o.number}`}
                              onClick={() => openPdf(`/production/${o.id}/bundle-label.pdf`).catch(() => toast.error("Bundle-Etikett konnte nicht geöffnet werden"))}>
                              <Printer size={14} className="mr-2" /> Bundle-Etikett
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem data-testid={`btn-edit-prod-${o.number}`} onClick={() => openEdit(o)}>
                            <Pencil size={14} className="mr-2" /> Bearbeiten
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem data-testid={`btn-delete-prod-${o.number}`} onClick={() => removeOrder(o)} className="text-red-600">
                            <Trash2 size={14} className="mr-2" /> Löschen
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="text-center py-12 text-sm text-[var(--text-muted)]" data-testid="production-empty">
                {orders.length === 0 ? "Noch keine Produktionsaufträge." : "Keine Aufträge für diesen Filter."}
              </div>
            )}
          </div>
        </div>
        );
      })()}

      {/* Shortage-Confirm Dialog */}
      <Dialog open={!!shortageDlg} onOpenChange={(o) => !o && !startBusy && setShortageDlg(null)}>
        <DialogContent className="max-w-lg" data-testid="dialog-shortage">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2 text-red-700">
              <AlertTriangle size={22} /> Fehlmenge — Start blockiert
            </DialogTitle>
            <DialogDescription>
              Der Auftrag <b>{shortageDlg?.order?.number}</b> hat Zutaten mit
              unzureichendem Bestand. Du kannst den Start dennoch erzwingen —
              der Fehlbestand wird beim Abschluss ohne Charge-Referenz gebucht.
            </DialogDescription>
          </DialogHeader>
          {shortageDlg && (
            <div className="space-y-3">
              <div className="border border-red-200 rounded-xl overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-red-50">
                    <tr>
                      <th className="text-left p-2">SKU</th>
                      <th className="text-left p-2">Zutat</th>
                      <th className="text-right p-2">Benötigt</th>
                      <th className="text-right p-2">Verfügbar</th>
                      <th className="text-right p-2">Fehlt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shortageDlg.shortages.map((s, i) => (
                      <tr key={i} data-testid={`shortage-row-${s.sku}`} className="border-t border-red-100">
                        <td className="p-2 font-mono">{s.sku}</td>
                        <td className="p-2">{s.name}</td>
                        <td className="p-2 text-right font-mono">{s.required} {s.unit}</td>
                        <td className="p-2 text-right font-mono">{s.available} {s.unit}</td>
                        <td className="p-2 text-right font-mono font-bold text-red-700">−{s.shortage} {s.unit}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShortageDlg(null)}
                        disabled={startBusy} className="rounded-full">Abbrechen</Button>
                <Button onClick={() => startProduction(shortageDlg.order, true)}
                        disabled={startBusy}
                        data-testid="btn-force-start"
                        className="rounded-full bg-red-600 text-white hover:bg-red-700 font-bold">
                  {startBusy ? "Starte…" : "Trotzdem starten"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Runde 126.8 – Box-Fehlmengen-Dialog */}
      <Dialog open={!!boxShortageDlg} onOpenChange={(o) => !o && setBoxShortageDlg(null)}>
        <DialogContent className="max-w-lg" data-testid="dialog-box-shortage">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2 text-amber-800">
              <AlertTriangle size={22} /> Verpackungs-Boxen fehlen
            </DialogTitle>
            <DialogDescription>
              Für den Abschluss werden Boxen benötigt, deren Bestand nicht ausreicht.
              Du kannst den Abschluss trotzdem erzwingen — die Fehlmenge wird ohne Charge-Referenz
              gebucht und muss vom Lager nachträglich ausgeglichen werden.
            </DialogDescription>
          </DialogHeader>
          {boxShortageDlg && (
            <div className="space-y-3">
              <div className="border border-amber-300 rounded-xl overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-amber-50">
                    <tr>
                      <th className="text-left p-2">Karton-SKU</th>
                      <th className="text-left p-2">Name</th>
                      <th className="text-right p-2">Benötigt</th>
                      <th className="text-right p-2">Bestand</th>
                      <th className="text-right p-2">Fehlt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {boxShortageDlg.shortages.map((s, i) => (
                      <tr key={i} data-testid={`box-shortage-row-${s.sku}`} className="border-t border-amber-100">
                        <td className="p-2 font-mono font-bold">{s.sku}</td>
                        <td className="p-2 text-[11px]">{s.name}</td>
                        <td className="p-2 text-right font-mono">{s.required}</td>
                        <td className="p-2 text-right font-mono">{s.available}</td>
                        <td className="p-2 text-right font-mono font-bold text-red-700">−{s.missing}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setBoxShortageDlg(null)} className="rounded-full">Abbrechen</Button>
                <Button onClick={forceComplete} data-testid="btn-force-complete"
                        className="rounded-full bg-amber-600 text-white hover:bg-amber-700 font-bold">
                  Trotzdem abschließen
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!completeFor} onOpenChange={(o) => { if (!o) { setCompleteFor(null); setShelfSug(null); } }}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">Produktion abschließen</DialogTitle></DialogHeader>
          <form onSubmit={complete} className="space-y-4">
            <div>
              <Label>Ist-Menge (produzierte + verpackte Tüten) *</Label>
              <Input required type="number" step="any"
                     placeholder={DECIMAL_HINT}
                     data-testid="input-complete-qty"
                     value={completeForm.actual_quantity}
                     onChange={(e) => {
                       const v = normalizeDecimal(e.target.value);
                       setCompleteForm((c) => ({
                         ...c, actual_quantity: v, _confirmed: false,
                         // Regal-Vorschlag mitziehen, solange der Nutzer die
                         // Zeilen noch nicht angefasst hat.
                         output_locations: (c._locations_touched ? c.output_locations : suggestLocations(v)),
                       }));
                     }} />
            </div>

            <div>
              <Label>Ausschuss-Tüten (defekt, MHD-Fehldruck)</Label>
              <Input type="number" step="any" min="0"
                     data-testid="input-complete-waste"
                     placeholder="0"
                     value={completeForm.waste_bags}
                     onChange={(e) => setCompleteForm({ ...completeForm, waste_bags: normalizeDecimal(e.target.value), _confirmed: false })} />
              <div className="text-[10px] text-[var(--text-muted)] mt-0.5">
                Wird <b>zusätzlich</b> vom Tüten-Bestand abgezogen und auf das Ausschusskonto mit Produktbezeichnung gebucht. Nicht Teil der Ist-Menge.
              </div>
            </div>

            {/* Runde 126.7 – Verpackungs-Aufschlüsselung */}
            <div className="rounded-xl border border-[var(--border)] p-3 bg-white/60 space-y-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)]">Verpackungs-Aufschlüsselung</div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-[11px]">Einzeltüten (lose)</Label>
                  <Input type="number" step="any" min="0"
                         data-testid="input-complete-bags"
                         placeholder="0"
                         value={completeForm.packaged_bags}
                         onChange={(e) => setCompleteForm({ ...completeForm, packaged_bags: normalizeDecimal(e.target.value) })} />
                </div>
                <div>
                  <Label className="text-[11px]">13er Displays</Label>
                  <Input type="number" step="1" min="0"
                         data-testid="input-complete-13er"
                         placeholder="0"
                         value={completeForm.packaged_13er}
                         onChange={(e) => setCompleteForm({ ...completeForm, packaged_13er: normalizeDecimal(e.target.value) })} />
                  <div className="text-[9px] text-[var(--text-muted)] mt-0.5">= {(Number(completeForm.packaged_13er)||0)*13} Tüten</div>
                </div>
                <div>
                  <Label className="text-[11px]">40er Kartons</Label>
                  <Input type="number" step="1" min="0"
                         data-testid="input-complete-40er"
                         placeholder="0"
                         value={completeForm.packaged_40er}
                         onChange={(e) => setCompleteForm({ ...completeForm, packaged_40er: normalizeDecimal(e.target.value) })} />
                  <div className="text-[9px] text-[var(--text-muted)] mt-0.5">= {(Number(completeForm.packaged_40er)||0)*40} Tüten</div>
                </div>
              </div>
              {(() => {
                const a = Number(completeForm.actual_quantity) || 0;
                const w = Number(completeForm.waste_bags) || 0;
                const pack = (Number(completeForm.packaged_bags)||0) + (Number(completeForm.packaged_13er)||0)*13 + (Number(completeForm.packaged_40er)||0)*40;
                if (a <= 0) return null;
                const sum = pack + w;
                const ok = Math.abs(sum - a) < 0.5;
                return (
                  <div className={`text-[11px] rounded px-2 py-1 ${ok ? "bg-emerald-50 text-emerald-800" : "bg-amber-100 text-amber-900"}`} data-testid="packaging-sum-check">
                    Verpackt {pack} + Ausschuss {w} = <b>{sum}</b> {ok ? `✓ = Ist-Menge ${a}` : `≠ Ist-Menge ${a} (Differenz ${sum - a})`}
                  </div>
                );
              })()}
            </div>

            {(() => {
              const a = Number(completeForm.actual_quantity) || 0;
              const w = Number(completeForm.waste_bags) || 0;
              if (a <= 0) return null;
              const b = computeBreakdown(a);
              const parts = [];
              if (b.pallets > 0) parts.push(`${b.pallets} Palette${b.pallets === 1 ? "" : "n"} × ${PALETTE_SIZE}`);
              if (b.kartons > 0) parts.push(`${b.kartons} Karton${b.kartons === 1 ? "" : "s"} × ${PACKS_PER_KARTON}`);
              if (b.loose   > 0) parts.push(`${b.loose} Einzeltüten`);
              return (
                <div className="text-xs rounded-lg px-3 py-2 bg-emerald-50 border border-emerald-300 text-emerald-900" data-testid="complete-breakdown">
                  <div className="font-semibold">Aufteilung: {parts.join(" + ")}</div>
                  <div className="text-[11px] mt-0.5 opacity-90">
                    Tüten-Bestand wird um <b>{a} + {w} = {a + w}</b> reduziert
                    {w > 0 && <> · davon <b>{w}</b> als Ausschuss gebucht</>}
                  </div>
                </div>
              );
            })()}

            {shelfSug && (
              <div data-testid="shelf-suggestion-banner"
                   className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-3 text-xs space-y-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold">Sorte:</span>
                  <span data-testid="shelf-sug-group">
                    {shelfSug.product_group || "keine hinterlegt"}
                    {shelfSug.group_code ? ` · WG ${shelfSug.group_code}` : ""}
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold">Empfohlene Regalreihe:</span>
                  <span className="font-mono font-bold text-[var(--pop-pink)]" data-testid="shelf-sug-best">
                    {shelfSug.best?.code || "—"}
                  </span>
                  {shelfSug.best?.co_located && (
                    <span className="text-[10px] text-[var(--text-muted)]">(gleiches Produkt liegt hier)</span>
                  )}
                </div>
                {shelfSug.best?.fefo_hint && (
                  <div className="text-[11px] text-amber-700 dark:text-amber-400" data-testid="shelf-sug-fefo">
                    FEFO: {shelfSug.best.fefo_hint}
                  </div>
                )}
                {shelfSug.warning && (
                  <div className="text-[11px] text-amber-700 dark:text-amber-400 flex items-center gap-1" data-testid="shelf-sug-warning">
                    <AlertTriangle size={12} /> {shelfSug.warning}
                  </div>
                )}
                {shelfSug.suggestions?.length > 1 && (
                  <div className="text-[10px] text-[var(--text-muted)]">
                    Alternativen: {shelfSug.suggestions.slice(1, 4).map((s) => s.code).join(", ")}
                  </div>
                )}
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Regal / Lagerplätze</Label>
                <button type="button" data-testid="btn-add-location"
                        className="text-xs font-semibold text-[var(--pop-pink)] hover:underline"
                        onClick={() => setCompleteForm((c) => ({
                          ...c, _locations_touched: true,
                          output_locations: [...(c.output_locations || []), { location: "", quantity: 0, kind: "" }],
                        }))}>
                  + Weiteres Regal
                </button>
              </div>
              <div className="space-y-2">
                {(completeForm.output_locations || []).map((row, idx) => (
                  <div key={idx} className="grid grid-cols-[1fr_100px_90px_28px] gap-2 items-center">
                    <Input placeholder="z.B. A-01-03"
                           data-testid={`input-loc-${idx}`}
                           value={row.location}
                           onChange={(e) => setCompleteForm((c) => {
                             const next = [...(c.output_locations || [])];
                             next[idx] = { ...next[idx], location: e.target.value };
                             return { ...c, output_locations: next, _locations_touched: true, _confirmed: false };
                           })} />
                    <Input type="number" step="any" min="0"
                           data-testid={`input-loc-qty-${idx}`}
                           value={row.quantity || ""}
                           onChange={(e) => setCompleteForm((c) => {
                             const next = [...(c.output_locations || [])];
                             next[idx] = { ...next[idx], quantity: Number(e.target.value) };
                             return { ...c, output_locations: next, _locations_touched: true, _confirmed: false };
                           })} />
                    <div className="text-[10px] font-mono text-[var(--text-muted)] truncate" title={row.kind}>{row.kind || "—"}</div>
                    <button type="button" data-testid={`btn-remove-loc-${idx}`}
                            className="text-red-600 hover:text-red-700 text-lg leading-none"
                            onClick={() => setCompleteForm((c) => ({
                              ...c, _locations_touched: true,
                              output_locations: (c.output_locations || []).filter((_, i) => i !== idx),
                            }))} title="Zeile entfernen">×</button>
                  </div>
                ))}
                {(completeForm.output_locations || []).length === 0 && (
                  <div className="text-xs text-[var(--text-muted)] italic">Ist-Menge eintragen → Vorschlag wird automatisch erzeugt.</div>
                )}
              </div>
              {shelfSug?.group_code && (() => {
                const bad = (completeForm.output_locations || []).filter(
                  (r) => r.location && rackGroupCode(r.location) && rackGroupCode(r.location) !== shelfSug.group_code);
                if (!bad.length) return null;
                return (
                  <div data-testid="shelf-mismatch-warning"
                       className="text-[11px] text-amber-700 dark:text-amber-400 flex items-start gap-1 mt-1">
                    <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                    <span>Achtung: {bad.map((b) => b.location).join(", ")} gehört zu einer anderen Sorte
                      (erwartet WG {shelfSug.group_code}). Einlagerung trotzdem möglich.</span>
                  </div>
                );
              })()}
              <div className="text-[10px] text-[var(--text-muted)] mt-1">
                Palette = {KARTONS_PER_PALETTE} × {PACKS_PER_KARTON} Tüten = {PALETTE_SIZE} Tüten. Zeilen frei anpassbar.
              </div>
            </div>

            <div><Label>Neue Chargen-Nr. *</Label><Input required data-testid="input-complete-charge" value={completeForm.output_charge_number} onChange={(e) => setCompleteForm({ ...completeForm, output_charge_number: e.target.value })} /></div>
            <div><Label>MHD *</Label><Input required type="date" data-testid="input-complete-mhd" value={completeForm.output_mhd} onChange={(e) => setCompleteForm({ ...completeForm, output_mhd: e.target.value })} /></div>

            <DialogFooter>
              <Button type="submit" data-testid="btn-save-complete" className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">Abschließen</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Production Order Dialog */}
      <Dialog open={!!editOrder} onOpenChange={(o) => !o && setEditOrder(null)}>
        <DialogContent data-testid="edit-production-dialog" className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Auftrag {editOrder?.number} bearbeiten</DialogTitle>
            <DialogDescription>
              Nur Metadaten werden aktualisiert. Chargen und Lagerbestand bleiben unverändert.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Geplante Menge</Label>
                <Input type="number" step="any" data-testid="input-edit-qty"
                       placeholder={DECIMAL_HINT}
                       value={editForm.planned_quantity}
                       onChange={(e) => setEditForm({ ...editForm, planned_quantity: normalizeDecimal(e.target.value) })} />
              </div>
              <div>
                <Label>Plandatum</Label>
                <Input type="date" data-testid="input-edit-date"
                       value={editForm.planned_date}
                       onChange={(e) => setEditForm({ ...editForm, planned_date: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                <SelectTrigger data-testid="select-edit-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="planned">Geplant</SelectItem>
                  <SelectItem value="in_progress">In Bearbeitung</SelectItem>
                  <SelectItem value="completed">Abgeschlossen</SelectItem>
                  <SelectItem value="cancelled">Storniert</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Bundle-Größe</Label>
              <Select value={editForm.bundle_code || "none"} onValueChange={(v) => setEditForm({ ...editForm, bundle_code: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="select-edit-bundle"><SelectValue placeholder="Kein Bundle" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Einzelprodukt (kein Bundle)</SelectItem>
                  {Object.entries(bundles).map(([code, cfg]) => (
                    <SelectItem key={code} value={code}>{cfg.label} · {cfg.units} Einheiten</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notizen</Label>
              <Input data-testid="input-edit-notes"
                     value={editForm.notes}
                     onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} />
            </div>
            <div className="rounded-lg bg-blue-50 border border-blue-200 p-2 text-xs text-blue-900">
              ⓘ <b>Lagerbestand unberührt:</b> Diese Bearbeitung ändert nur den Auftragsdatensatz. Chargen, Stock-Movements und Bestände werden nicht neu berechnet.
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditOrder(null)} className="rounded-full">Abbrechen</Button>
              <Button type="submit" data-testid="btn-save-edit-production"
                      className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
                Speichern
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign to Sales Orders */}
      <Dialog open={!!assignFor} onOpenChange={(o) => !o && !assignBusy && (setAssignFor(null), setAssignSelected({}))}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="dialog-assign-order">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Link2 size={22} className="text-[var(--pop-pink)]" />
              Charge zuweisen — {assignFor?.output_charge_number || assignFor?.number}
            </DialogTitle>
            <DialogDescription>
              Wähle eine oder mehrere Bestellungen, denen die produzierte Charge zugewiesen werden soll.
              In der Bestellansicht erscheint danach die Charge inkl. Produktionsdatum & MHD.
            </DialogDescription>
          </DialogHeader>
          {assignFor && (
            <div className="space-y-3">
              <div className="bg-[var(--pop-cream)]/40 rounded-xl p-3 text-sm flex flex-wrap gap-x-6 gap-y-1">
                <div><span className="text-[var(--text-muted)]">Produkt:</span> <b>{assignFor.output_item_name}</b></div>
                <div><span className="text-[var(--text-muted)]">Charge:</span> <span className="font-mono">{assignFor.output_charge_number}</span></div>
                <div><span className="text-[var(--text-muted)]">MHD:</span> <span className="font-mono">{assignFor.output_mhd}</span></div>
                <div><span className="text-[var(--text-muted)]">Menge:</span> <span className="font-mono">{assignFor.actual_quantity}</span></div>
              </div>
              {assignOrders.length === 0 && (
                <div className="text-center py-8 text-[var(--text-muted)] text-sm">
                  Keine offenen Bestellungen verfügbar.
                </div>
              )}
              <div className="border border-[var(--border)] rounded-xl overflow-hidden">
                {assignOrders.map((so) => {
                  const already = so.assigned_production_batch;
                  return (
                    <label key={so.id}
                           data-testid={`assign-so-${so.number}`}
                           className={`flex items-start gap-2 p-3 border-b border-[var(--border)]/50 last:border-b-0 cursor-pointer hover:bg-[var(--pop-cream)]/30 ${assignSelected[so.id] ? "bg-[var(--pop-yellow)]/10" : ""}`}>
                      <input type="checkbox" checked={!!assignSelected[so.id]}
                             onChange={(e) => setAssignSelected({ ...assignSelected, [so.id]: e.target.checked })}
                             className="mt-1" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold text-sm">{so.number}</span>
                          <StatusBadge status={so.status} />
                          {already && (
                            <span className="pop-pill bg-orange-100 text-orange-800 text-[10px] font-bold" title="Bereits zugewiesen">
                              schon zugewiesen: {already.charge_number}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-[var(--text-muted)] mt-0.5">
                          {so.customer_name || "—"} · {(so.order_date || "").slice(0, 10)}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setAssignFor(null); setAssignSelected({}); }}
                        className="rounded-full" disabled={assignBusy}>Abbrechen</Button>
                <Button onClick={submitAssign} disabled={assignBusy}
                        data-testid="btn-confirm-assign"
                        className="rounded-full bg-[var(--pop-pink)] text-white hover:opacity-90 font-bold">
                  {assignBusy ? "Weise zu…" : "Zuweisen"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Pickliste Dialog */}
      <Dialog open={!!pickOrder} onOpenChange={(o) => !o && (setPickOrder(null), setPickData(null))}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <ClipboardList size={22} className="text-[var(--pop-pink)]" />
              Pickliste – {pickOrder?.number}
            </DialogTitle>
            <DialogDescription>
              Materialbedarf und FEFO-Chargen-Vorschlag für diesen Produktionsauftrag.
            </DialogDescription>
          </DialogHeader>
          {pickLoading && <div className="py-10 text-center text-[var(--text-muted)]">Lade Pickliste…</div>}
          {pickData && (
            <div className="space-y-4">
              <div className="bg-[var(--pop-cream)]/40 border border-[var(--border)] rounded-xl p-3 text-sm flex flex-wrap gap-x-6 gap-y-1">
                <div><span className="text-[var(--text-muted)]">Rezeptur:</span> <b>{pickData.recipe?.name}</b></div>
                <div><span className="text-[var(--text-muted)]">Produkt:</span> {pickData.recipe?.output_item_name}</div>
                <div><span className="text-[var(--text-muted)]">Menge:</span> {pickData.order?.planned_quantity} × Rezept-Ausbeute {pickData.recipe?.output_quantity}</div>
                {pickData.picked_at && (
                  <div className="text-[var(--success)] font-semibold">✓ Bereits kommissioniert am {new Date(pickData.picked_at).toLocaleString("de-DE")} · {pickData.picked_by_name}</div>
                )}
              </div>

              {pickData.any_shortage && !pickData.picked_at && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-800 flex items-start gap-2" data-testid="pick-shortage-warning">
                  <AlertTriangle size={18} className="mt-0.5 flex-shrink-0" />
                  <div><b>Fehlmenge:</b> Der freigegebene Bestand einiger Zutaten reicht nicht aus. Bestätigen bucht trotzdem – Fehlmengen werden ohne Charge protokolliert. Prüfe Chargen-Freigabe (QC) auf der Chargen-Seite.</div>
                </div>
              )}

              <div className="border border-[var(--border)] rounded-xl overflow-hidden">
                <Table className="table-zebra">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="overline">SKU</TableHead>
                      <TableHead className="overline">Zutat</TableHead>
                      <TableHead className="overline text-right">Benötigt</TableHead>
                      <TableHead className="overline text-right">Bestand</TableHead>
                      <TableHead className="overline">FEFO-Chargen (Vorschlag)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pickData.lines.map((l) => (
                      <TableRow key={l.ingredient_item_id} data-testid={`pick-line-${l.sku}`}>
                        <TableCell className="font-mono text-xs">{l.sku}</TableCell>
                        <TableCell>
                          {l.name}
                          {l.reserved_for_others > 0 && (
                            <div className="text-[10px] text-orange-700 font-semibold mt-0.5">
                              ⓘ {l.reserved_for_others} {l.unit} reserviert für andere Aufträge
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm font-bold">{l.required_qty} {l.unit}</TableCell>
                        <TableCell className={`text-right font-mono text-xs ${l.coverage_ok ? "" : "text-[var(--danger)]"}`}>{l.current_stock} {l.unit}</TableCell>
                        <TableCell className="text-xs">
                          {l.picks.length === 0 && (
                            <span className="text-[var(--danger)] font-semibold">Keine FEFO-Charge verfügbar (Fehlmenge {l.shortage} {l.unit})</span>
                          )}
                          {l.picks.map((p, i) => (
                            <div key={i} className="font-mono">
                              <b>{p.charge_number}</b> · MHD {p.mhd} · nimm {p.take_qty} {l.unit}
                              {p.location ? ` · Regal ${p.location}` : ""}
                            </div>
                          ))}
                        </TableCell>
                      </TableRow>
                    ))}
                    {pickData.lines.length === 0 && (
                      <TableRow><TableCell colSpan={5} className="text-center py-8 text-[var(--text-muted)]">Rezeptur hat keine Zutaten.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => printPicklist(pickOrder)}
                        data-testid="btn-picklist-pdf" className="rounded-full">
                  <Printer size={14} className="mr-1" /> Als PDF drucken
                </Button>
                {!pickData.picked_at && pickData.lines.length > 0 && (
                  <Button onClick={confirmPick} disabled={confirming}
                          data-testid="btn-confirm-pick"
                          className="rounded-full bg-[var(--pop-pink)] text-white hover:opacity-90 font-bold">
                    <CheckCircle2 size={16} className="mr-1" />
                    {confirming ? "Buche…" : "Bestätigen & Bestand abziehen"}
                  </Button>
                )}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
